// Batches online-ordering UX events (button usage, blocked states, JS/AJAX
// errors) and flushes them off the main thread via sendBeacon so tracking
// never adds perceptible delay to a click. Scoped entirely to whichever
// layout mounts app/javascript/controllers/ux_tracking_controller.js — not
// wired up anywhere else in the app.
const FLUSH_INTERVAL_MS = 8000;
const MAX_QUEUE_SIZE = 50;

let endpoint = null;
let layout = null;
let queue = [];
let flushTimer = null;

export function initUxTracking({ endpoint: url, layout: layoutName }) {
  endpoint = url;
  layout = layoutName;
  queue = [];
  window.addEventListener("error", handleWindowError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", flush);
}

export function teardownUxTracking() {
  flush();
  clearInterval(flushTimer);
  flushTimer = null;
  window.removeEventListener("error", handleWindowError);
  window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("pagehide", flush);
  endpoint = null;
  layout = null;
  queue = [];
}

export function trackUxEvent(eventType, category, element, extra = {}) {
  if (!endpoint) return;

  queue.push({
    event_type: eventType,
    category,
    element,
    path: window.location.pathname,
    occurred_at: Date.now(),
    ...extra,
  });

  if (queue.length > MAX_QUEUE_SIZE) {
    queue.splice(0, queue.length - MAX_QUEUE_SIZE);
  }
}

// Fires an event once per element for server-rendered states that aren't the
// result of a click. Defaults to a `blocked` event (e.g. the "ordering
// closed" modal); data-track-impression-type="interaction" instead records an
// exposure (e.g. the order-type modal being shown, so its abandonment rate is
// measurable). Safe to call repeatedly (after AJAX content swaps, or from the
// shown.bs.modal rescan) — already-fired elements are skipped via a marker
// attribute, and elements that are in the DOM but not displayed (a modal
// that was never opened) are left unmarked so a later scan can catch them
// once they actually appear.
const IMPRESSION_SELECTOR =
  "[data-track-impression]:not([data-track-impression-fired])";

export function scanImpressions(root = document) {
  const elements = Array.from(root.querySelectorAll(IMPRESSION_SELECTOR));
  if (root instanceof Element && root.matches(IMPRESSION_SELECTOR)) {
    elements.unshift(root);
  }

  elements.forEach((element) => {
    if (element.getClientRects().length === 0) return;

    element.setAttribute("data-track-impression-fired", "true");
    const [category, name] = element.dataset.trackImpression.split(".");
    const eventType = element.dataset.trackImpressionType || "blocked";
    // Keyed on the attribute, not the event type: interaction-style exposures
    // (e.g. the Uber fee with its bucket) carry a reason too.
    const extra = element.dataset.trackImpressionReason
      ? { reason: element.dataset.trackImpressionReason }
      : {};
    trackUxEvent(eventType, category, name, extra);
  });
}

// Delegated listeners scoped to a layout root element, not document-wide —
// bound/unbound per Stimulus connect()/disconnect() since Turbolinks
// re-visits the page without a full reload.
export function bindInteractionListeners(root) {
  root.addEventListener("click", handleClick, true);
  root.addEventListener("submit", handleSubmit, true);
  root.addEventListener("ajax:error", handleAjaxError, true);
  root.addEventListener("shown.bs.modal", handleModalShown, true);
}

export function unbindInteractionListeners(root) {
  root.removeEventListener("click", handleClick, true);
  root.removeEventListener("submit", handleSubmit, true);
  root.removeEventListener("ajax:error", handleAjaxError, true);
  root.removeEventListener("shown.bs.modal", handleModalShown, true);
}

// Impressions inside a modal (or on the modal itself) only count once the
// modal is actually displayed — connect-time scans skip hidden elements.
function handleModalShown(event) {
  scanImpressions(event.target);
}

function handleClick(event) {
  const target = event.target.closest("[data-track]");
  // Forms are tracked via their submit event, not the click that triggers
  // it, to avoid double-counting a single add-to-cart tap.
  if (!target || target.tagName === "FORM") return;
  dispatchInteraction(target);
}

function handleSubmit(event) {
  // The submit event target is always the <form> itself.
  const target = event.target;
  if (!target.matches || !target.matches("[data-track]")) return;
  dispatchInteraction(target);
}

function dispatchInteraction(target) {
  const [category, element] = target.dataset.track.split(".");
  const disabled =
    target.classList.contains("disabled") ||
    target.disabled === true ||
    target.getAttribute("aria-disabled") === "true";

  if (disabled) {
    trackUxEvent("blocked", category, element, { reason: "disabled" });
  } else {
    trackUxEvent("interaction", category, element);
  }
}

function handleAjaxError(event) {
  const detail = event.detail || [];
  const xhr = detail[2];
  const target = event.target;

  trackUxEvent("ajax_error", "ajax", "request_failed", {
    status: xhr ? xhr.status : undefined,
    ajax_url:
      (target && (target.getAttribute("action") || target.getAttribute("href"))) ||
      undefined,
  });
}

function handleWindowError(event) {
  trackUxEvent("js_error", "js", "window_error", {
    message: event.message ? String(event.message).slice(0, 500) : undefined,
    source: event.filename ? String(event.filename).slice(0, 255) : undefined,
    lineno: event.lineno,
    colno: event.colno,
    stack:
      event.error && event.error.stack
        ? String(event.error.stack).slice(0, 2000)
        : undefined,
  });
}

function handleUnhandledRejection(event) {
  const reason = event.reason;
  const message =
    reason && reason.message ? String(reason.message) : String(reason);

  trackUxEvent("js_error", "js", "unhandled_rejection", {
    message: message.slice(0, 500),
    stack: reason && reason.stack ? String(reason.stack).slice(0, 2000) : undefined,
  });
}

function handleVisibilityChange() {
  if (document.visibilityState === "hidden") flush();
}

function flush() {
  if (!endpoint || queue.length === 0) return;

  const events = queue;
  queue = [];
  const payload = JSON.stringify({ layout, events });

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon(endpoint, blob)) return;
  }

  // Fire-and-forget fallback — never awaited, never blocks the caller.
  fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {});
}
